/**
 * Shared Result / ServiceError types for service layer (see services.instructions.md Section: Result Pattern).
 * Centralizes discriminated union to avoid duplication and drift.
 */

export type ServiceErrorCode =
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'PRECONDITION'
  | 'UNAUTHENTICATED'
  | 'EXTERNAL'
  | 'UNKNOWN';

export interface ServiceError {
  code: ServiceErrorCode;
  message: string;
  details?: unknown;
}

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: ServiceError };

// Helpers
export const ok = <T>(value: T): Result<T> => ({ ok: true, value });
export const err = (
  code: ServiceErrorCode,
  message: string,
  details?: unknown,
): Result<never> => ({ ok: false, error: { code, message, details } });

export const isOk = <T>(r: Result<T>): r is { ok: true; value: T } => r.ok;
export const isErr = <T>(
  r: Result<T>,
): r is { ok: false; error: ServiceError } => !r.ok;

/** Narrow extractor useful when logging unknown exceptions */
export function extractErrorDetails(e: unknown): unknown {
  if (typeof e === 'object' && e !== null) return e;
  return String(e);
}
